import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth } from '@/lib/firebase';
import { API_URL } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import logoImg from '@/logo.png';
import logoWhiteImg from '@/logo-white.png';

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const token = searchParams.get('token');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  const currentLogo = theme === 'dark' ? logoWhiteImg : logoImg;

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('Token não fornecido');
      return;
    }

    const verify = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (!response.ok) {
          setStatus('error');
          setErrorMsg(data.error || 'Falha ao verificar email');
          return;
        }

        // Force token refresh if user is logged in
        if (auth.currentUser) {
          await auth.currentUser.getIdToken(true);
          await auth.currentUser.reload();
        }

        setStatus('success');
      } catch {
        setStatus('error');
        setErrorMsg('Erro ao verificar email. Tente novamente.');
      }
    };

    verify();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img src={currentLogo} alt="Cataloga" className="h-20 w-auto" />
            </div>
          </div>

          <Card className="backdrop-blur-lg bg-white/90 dark:bg-gray-800/90 border-0 shadow-2xl">
            {status === 'loading' && (
              <>
                <CardHeader className="text-center pb-6">
                  <div className="flex justify-center mb-4">
                    <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
                  </div>
                  <CardTitle className="text-2xl font-bold">
                    Verificando email...
                  </CardTitle>
                  <CardDescription>
                    Aguarde enquanto confirmamos seu email.
                  </CardDescription>
                </CardHeader>
              </>
            )}

            {status === 'success' && (
              <>
                <CardHeader className="text-center pb-6">
                  <div className="flex justify-center mb-4">
                    <CheckCircle2 className="h-12 w-12 text-green-500" />
                  </div>
                  <CardTitle className="text-2xl font-bold">
                    Email confirmado!
                  </CardTitle>
                  <CardDescription>
                    Sua conta foi ativada com sucesso. Agora você pode usar a plataforma.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => {
                      if (auth.currentUser?.emailVerified) {
                        navigate('/');
                      } else {
                        navigate('/login');
                      }
                    }}
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold"
                  >
                    {auth.currentUser?.emailVerified
                      ? t('auth.goToApp', 'Ir para o app')
                      : t('auth.backToLogin', 'Fazer login')}
                  </Button>
                </CardContent>
              </>
            )}

            {status === 'error' && (
              <>
                <CardHeader className="text-center pb-6">
                  <div className="flex justify-center mb-4">
                    <AlertCircle className="h-12 w-12 text-red-500" />
                  </div>
                  <CardTitle className="text-2xl font-bold">
                    Erro na verificação
                  </CardTitle>
                  <CardDescription>{errorMsg}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold"
                  >
                    {t('auth.backToLogin', 'Voltar ao login')}
                  </Button>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
